import { inject, Injectable } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { CountryApiService, District, GenericStoreStatus, Ward } from '@us/web/shared/data-access';
import { filter, Observable, switchMap, tap } from 'rxjs';

export interface DeliveryAddressProps {
    districts: District[];
    wards: Ward[];
    selectedDistrict: District | null;
    selectedWard: Ward | null;
    status: GenericStoreStatus;
}

const initState: DeliveryAddressProps = {
    districts: [],
    wards: [],
    selectedDistrict: null,
    selectedWard: null,
    status: 'pending'
};

@Injectable()
export class DeliveryAddressStore extends ComponentStore<DeliveryAddressProps> {
    private readonly countryApiService = inject(CountryApiService);

    constructor() {
        super({ ...initState });

        this.loadDistricts();
        this.loadWards(this.selectedDistrict$);
    }

    readonly districts$: Observable<District[]> = this.select(state => state.districts);
    readonly selectedDistrict$: Observable<District | null> = this.select(state => state.selectedDistrict);

    readonly wards$: Observable<Ward[]> = this.select(state => state.wards);
    readonly selectedWard$: Observable<Ward | null> = this.select(state => state.selectedWard);

    readonly loadDistricts = this.effect(params$ => {
        return params$.pipe(
            tap(() => this.patchState({ status: 'loading' })),
            switchMap(() =>
                this.countryApiService.getDistricts().pipe(
                    tapResponse(
                        districts => {
                            this.patchState({
                                districts,
                                status: 'success'
                            });
                        },
                        error => this.patchState({ status: 'error' })
                    )
                )
            )
        );
    });

    readonly loadWards = this.effect((selectedDistrict$: Observable<District | null>) => {
        return selectedDistrict$.pipe(
            filter(district => district !== null),
            tap(() => this.patchState({ status: 'loading' })),
            switchMap(district =>
                this.countryApiService.getWards((district as District).id).pipe(
                    tapResponse(
                        wards => {
                            this.patchState({
                                wards,
                                status: 'success'
                            });
                        },
                        error => this.patchState({ status: 'error' })
                    )
                )
            )
        );
    });

    selectDistrict(district: District) {
        this.patchState({ selectedDistrict: district });
    }

    selectWard(ward: Ward) {
        this.patchState({ selectedWard: ward });
    }
}
